type UniqueComparator<T> = (prev: T, next: T) => boolean;

export const uniqueConsecutive = <T>(input: readonly T[], comparator: UniqueComparator<T> = defaultUniqueComparator) =>
  // Compress identical consecutive values to account for
  // multiple renders
  input.reduce((arr, value) => {
    if (arr.length === 0 || !comparator(arr[arr.length - 1], value)) {
      return [...arr, value];
    } else {
      return arr;
    }
  }, [] as T[]);

const defaultUniqueComparator = <T>(prev: T, next: T) => prev === next;

/**
 * Formats an array of strings in a grammatically correct way.
 * The patterns can include '{0}' and '{1}' tokens to represent items in the array.
 *
 * Note: why not `Intl.ListFormat`? Because as of 22.09.2020 the support is poor - only a couple
 * of latest versions of Chrome and FF support it. Polyfilling is quite complicated due to i18n,
 * and even if we can determine the current browser locale, the user might choose a different locale
 * to display content. With built-in `Intl` this is not a problem because browsers include all
 * locales. But with the polyfill we have to include locales manually and this is quite
 * complicated. As such an easier solution is to just implement a subset of `Intl.ListFormat`
 * ourselves.
 */
export const formatArray = (
  list: string[],
  patterns: {
    two: string;
    start: string;
    middle: string;
    end: string;
  } = {
    two: '{0}, {1}',
    start: '{0}, {1}',
    middle: '{0}, {1}',
    end: '{0}, {1}',
  },
) => {
  if (list.length === 0) {
    return '';
  }

  if (list.length === 1) {
    return list[0];
  }

  let last = list.length - 1;

  if (list.length === 2) {
    return patterns.two.replace('{0}', list[0]).replace('{1}', list[last]);
  }

  let result = patterns.end.replace('{1}', list[last]);

  while (last > 2) {
    last -= 1;
    const middle = patterns.middle.replace('{1}', list[last]);
    result = result.replace('{0}', middle);
  }

  result = result.replace('{0}', list[last - 1]);
  result = patterns.start.replace('{1}', result);
  result = result.replace('{0}', list[last - 2]);

  return result;
};
